import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import {Observable, of, ReplaySubject} from 'rxjs';
import {
    administrationNavigation,
    billingNavigation, billingNavigationCaisse,
    defaultNavigation,
    laboratoireNavigation,
    patientManagementNavigation,
    pharmacieNavigation,
    stockNavigation
} from "../../mock-api/common/navigation/data";
import {FuseNavigationItem} from "../../../@fuse/components/navigation";
import Keycloak from "keycloak-js";
import {environment} from "../../../environments/environment";

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    constructor(private keycloak: Keycloak) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        const isPharmacyOnly = this.keycloak.hasRealmRole('PHARMACIE') && !this.keycloak.hasRealmRole('ADMIN');
        const items: FuseNavigationItem[] = [...(isPharmacyOnly ? patientManagementNavigation : defaultNavigation)];

        const hasHospitalizationAccess = environment.hospitalizationAccess.some(role => this.keycloak.hasRealmRole(role));
        if (!hasHospitalizationAccess) {
            const patientManagementItem = defaultNavigation[0].children.find(item => item.id === 'general_patient_management');
            if (patientManagementItem?.children) {
                patientManagementItem.children = patientManagementItem.children.filter(item => item.id !== 'hospitalizations');
            }
        }

        if (this.keycloak.hasRealmRole("MEDECIN")) {
            const planning = defaultNavigation[0].children.find(item => item.id === 'planning');
            if (planning?.children && !planning.children.some(item => item.id === 'doctor_appointments')) {
                planning.children.push({
                    id: 'doctor_appointments',
                    title: 'Mes rendez-vous',
                    type: 'basic',
                    icon: 'heroicons_outline:calendar',
                    link: '/planning/doctors',
                });
            }

            const patientManagement = defaultNavigation[0].children.find(item => item.id === 'general_patient_management');
            if (patientManagement?.children && !patientManagement.children.some(item => item.id === 'doctor_admissions')) {
                const admissionIndex = patientManagement.children.findIndex(item => item.id === 'admissions');
                const targetIndex = admissionIndex >= 0 ? admissionIndex + 1 : patientManagement.children.length;
                patientManagement.children.splice(targetIndex, 0, {
                    id: 'doctor_admissions',
                    title: 'Mes Consultations',
                    type: 'basic',
                    icon: 'heroicons_outline:document-text',
                    link: '/doctor-admissions',
                });
            }
        }

        if (this.keycloak.hasRealmRole('INFIRMIER') || this.keycloak.hasRealmRole('ADMIN') || this.keycloak.hasRealmRole('GESTIONNAIRE')) {
            const patientManagement = defaultNavigation[0].children.find(item => item.id === 'general_patient_management');
            if (patientManagement?.children && !patientManagement.children.some(item => item.id === 'nurse_admissions')) {
                const admissionIndex = patientManagement.children.findIndex(item => item.id === 'admissions');
                const targetIndex = admissionIndex >= 0 ? admissionIndex + 1 : patientManagement.children.length;
                patientManagement.children.splice(targetIndex, 0, {
                    id: 'nurse_admissions',
                    title: 'Admissions du jour',
                    type: 'basic',
                    icon: 'heroicons_outline:clipboard-document-list',
                    link: '/nurse-admissions',
                });
            }
        }

        const pushUnique = (item: FuseNavigationItem): void => {
            if (!items.some(existing => existing.id === item.id)) {
                items.push(item);
            }
        };

        if (!isPharmacyOnly && (this.keycloak.hasRealmRole("COMPTABLE") || this.keycloak.hasRealmRole("ADMIN") || this.keycloak.hasRealmRole("GESTIONNAIRE"))) {
            pushUnique(billingNavigation);
        }

        if (!isPharmacyOnly && (this.keycloak.hasRealmRole("CAISSE") || this.keycloak.hasRealmRole("ACCUEIL"))) {
            pushUnique(billingNavigationCaisse);
        }

        if (!isPharmacyOnly && this.keycloak.hasRealmRole('ADMIN') || this.keycloak.hasRealmRole('STOCK') || this.keycloak.hasRealmRole('GESTIONNAIRE')) {
            pushUnique(stockNavigation);
        }

        if (this.keycloak.hasRealmRole('PHARMACIE') || this.keycloak.hasRealmRole('ADMIN') || this.keycloak.hasRealmRole('STOCK') || this.keycloak.hasRealmRole('GESTIONNAIRE')) {
            pushUnique(pharmacieNavigation);
        }

        if (!isPharmacyOnly && (this.keycloak.hasRealmRole('LABORANTIN') || this.keycloak.hasRealmRole('ADMIN') || this.keycloak.hasRealmRole('GESTIONNAIRE'))) {
            pushUnique(laboratoireNavigation);
        }

        if (this.keycloak.hasRealmRole('STOCK')) {
            let tmp = laboratoireNavigation;
            tmp.children = tmp.children.filter(item => item.id !== 'laboratoire_analysis');
            pushUnique(tmp);
        }

        if(!isPharmacyOnly && this.keycloak.hasRealmRole("ADMIN")) {
            pushUnique(administrationNavigation);
        }

        let nav: Navigation = {
            compact: items,
            default: items,
            futuristic: items,
            horizontal: items
        }

        this._navigation.next(nav);
        return of(nav);
    }
}
